import { Box, useTheme, Stack, Grid } from "@mui/material";
import {
  AvailablePaymentCurrency,
  formatSmartNarrowCurrencyFromCents,
  Locales,
  GIFT_AID_PLUS_VALUE_PERCENTAGE,
} from "@simplyk/common";

import { mergeSx } from "@/common/helpers/mergeSx";
import { Trans, useTranslate } from "@/common/hooks/useTranslate";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import GiftAidArrow from "@/icons/illustration/giftaid_arrow.svg";
import GiftAidLogo from "@/icons/illustration/giftaid_logo.svg";

interface GiftAidDescriptionProps {
  amount: number;
  id?: string;
}

export const GiftAidDescription = ({ amount, id }: GiftAidDescriptionProps) => {
  const theme = useTheme();
  const { t } = useTranslate();

  if (!theme.palette.forms || !theme.palette.formsComponents?.giftAidBlock) {
    return <InvalidThemeErrorMessage />;
  }

  const styles = mergeSx({
    color: theme.palette.formsComponents.giftAidBlock.contentTextColor,
  });

  return (
    <Box id={id} sx={styles}>
      <Stack>
        <Stack direction="row">
          <Grid container alignItems="center" marginBottom={1}>
            <Grid item xs>
              <Stack direction="row" spacing={1} alignItems="center">
                <FormTypography
                  variant="h5"
                  component="span"
                  mr={1}
                  color={theme.palette.formsComponents.giftAidBlock.headerTextColorReverse}
                >
                  {formatSmartNarrowCurrencyFromCents(amount, {
                    locale: Locales.EN,
                    currency: AvailablePaymentCurrency.Gbp,
                  })}
                </FormTypography>
                <GiftAidArrow
                  style={{ color: theme.palette.formsComponents.giftAidBlock.headerTextColor }}
                  width={28}
                  height={16}
                />
                <FormTypography
                  variant="h5"
                  color={theme.palette.formsComponents.giftAidBlock.headerTextColor}
                  component="span"
                  ml={1}
                >
                  {formatSmartNarrowCurrencyFromCents((1 + GIFT_AID_PLUS_VALUE_PERCENTAGE) * amount, {
                    locale: Locales.EN,
                    currency: AvailablePaymentCurrency.Gbp,
                  })}
                </FormTypography>
              </Stack>
            </Grid>
            <Grid item>
              <Stack direction="column" sx={{ flex: 0, alignItems: "center", justifyContent: "center" }}>
                <GiftAidLogo
                  style={{ color: theme.palette.formsComponents.giftAidBlock.headerTextColor }}
                  width={100}
                  height={32}
                />
              </Stack>
            </Grid>
          </Grid>
        </Stack>
        <FormTypography variant="subtitle1" sx={{ mb: 1 }}>
          {t("donationForm", "giftAidBlock.description.subtitle")}
        </FormTypography>
        <FormTypography variant="body2" sx={{ mb: 1.5 }} component="span">
          <Trans
            space="donationForm"
            i18nKey="giftAidBlock.description.body"
            components={{ a: <FormTypography variant="body2" component="span" vibe="brand" /> }}
          />
        </FormTypography>
      </Stack>
    </Box>
  );
};
